import { useEffect, useCallback, useRef } from 'react';
import map from 'lodash/map';
import DOMPurify from 'dompurify';
import type { ChartData } from '../../../common/summary/types.tsx';
import { GRAPH_EVENT_TYPES, GRAPH_ACTION_TYPES } from './constants';
import type { EChartsInstance, EChartsOption, EChartsEvent } from './types';

type Props = {
	onPointSelect?: (arg1: EChartsEvent) => void;
	source: ChartData;
};

/**
 * useChartEvents
 * this hook handles all chart events like mouse-over, mouse-out & click events
 * on any point select onPointSelect callback function will get invoked with point details
 */
export const useChartEvents = ({ onPointSelect, source }: Props) => {
	const chartRef = useRef<unknown>(undefined);
	const highlightedPointRef = useRef<unknown>(undefined);

	const setCurrentHighlightedPoint = useCallback(() => {
		highlightedPointRef.current &&
			chartRef.current &&
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			chartRef.current.dispatchAction(highlightedPointRef.current);
	}, []);

	const highlightPoint = useCallback(
		(params: EChartsEvent) => {
			if (!chartRef.current) {
				return;
			}
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			chartRef.current.dispatchAction({
				type: GRAPH_ACTION_TYPES.DOWNPLAY_STALE_POINT,
			});
			highlightedPointRef.current = {
				type: GRAPH_ACTION_TYPES.HIGHLIGHT_SELECTED_POINT,
				seriesIndex: params.seriesIndex,
				seriesName: params.seriesName,
				dataIndex: params.dataIndex,
				name: params.name,
			};
			setCurrentHighlightedPoint();
		},
		[setCurrentHighlightedPoint],
	);

	const downplayPoint = useCallback(() => {
		if (!chartRef.current) {
			return;
		}
		// @ts-expect-error - TS2571 - Object is of type 'unknown'.
		chartRef.current.dispatchAction({
			type: GRAPH_ACTION_TYPES.DOWNPLAY_STALE_POINT,
		});
		highlightedPointRef.current = undefined;
	}, []);

	const refreshOptions = useCallback((config: EChartsOption) => {
		chartRef.current &&
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			chartRef.current.setOption(config, {
				notMerge: true,
			});
	}, []);

	const activateTooltipOnMouseOver = useCallback(
		(params: EChartsEvent) => {
			chartRef.current &&
				// @ts-expect-error - TS2571 - Object is of type 'unknown'.
				chartRef.current.dispatchAction({
					type: GRAPH_ACTION_TYPES.SHOW_TOOL_TIP,
					seriesIndex: params.seriesIndex,
					// @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string | Node'.
					seriesName: DOMPurify.sanitize(params.seriesName),
					dataIndex: params.dataIndex,
					name: params.name,
				});
			// highlighted previous selected chart point got
			setCurrentHighlightedPoint();
		},
		[setCurrentHighlightedPoint],
	);

	const hideTooltipOnMouseOut = useCallback(() => {
		chartRef.current &&
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			chartRef.current.dispatchAction({
				type: GRAPH_ACTION_TYPES.HIDE_TIP,
			});
		chartRef.current &&
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			chartRef.current.dispatchAction({
				type: GRAPH_ACTION_TYPES.DOWNPLAY_STALE_POINT,
			});
		setCurrentHighlightedPoint();
	}, [setCurrentHighlightedPoint]);

	// TODO: Hacky code because Echarts doesn't support a11y. We have to do all these to get access to the series to trigger mouse like events on keypress. Remove it after highcharts are implemented
	const findSeries = useCallback(
		(echartsInstance: EChartsInstance, path: Path2D) => {
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			const color = path.getAttribute('stroke');
			const transform = path
				// @ts-expect-error - TS2571 - Object is of type 'unknown'.
				.getAttribute('transform')
				.split('matrix(')[1]
				.split(')')[0]
				.split(',');
			const transformX = Math.round(transform[4] * 100) / 100;
			const transformY = Math.round(transform[5] * 100) / 100;
			const seriesIndex = source.chartSeries.findIndex(
				(series) => series.itemStyle.color === color,
			);
			const series = source.chartSeries[seriesIndex];
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			const seriesInstance = Object.values(echartsInstance._chartsMap).find(
				// @ts-expect-error - TS2571 - Object is of type 'unknown'.
				(instance) => instance._data._visual.style.fill === color,
			);
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			const index = seriesInstance._data._graphicEls.findIndex((item) => {
				if (
					Math.round(item.x * 100) / 100 === transformX &&
					Math.round(item.y * 100) / 100 === transformY
				) {
					return true;
				}
				return false;
			});

			return { data: series.data[index], seriesName: series.name };
		},
		[source.chartSeries],
	);

	// register chart events on chart mount
	const registerEvents = useCallback(
		(echartsInstance: EChartsInstance) => {
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			const paths = chartRef.current._dom.querySelectorAll('path[d="M 1 0 A 1 1 0 1 1 1 -0.0001"]');

			for (let pathIndex = 0; pathIndex < paths.length; pathIndex++) {
				paths[pathIndex].setAttribute('tabindex', 0);
			}

			// NOTE: when this event listener is removed when migrating to the new charting library
			// please remove the "error.stack?.includes('reports-common')" condition in
			// src/packages/platform/test-utils/test-cleanup/src/common/utils/symmetric-listeners.tsx
			// see https://atlassian.slack.com/archives/CL6HC337Z/p1708926151196649?thread_ts=1708914082.486629&cid=CL6HC337Z

			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			chartRef.current._dom.addEventListener('keydown', (event) => {
				if (
					event.key === 'Enter' &&
					event.target.nodeName === 'path' &&
					event.target.getAttribute('tabindex')
				) {
					const { data } = findSeries(echartsInstance, event.target);
					if (onPointSelect) {
						onPointSelect({ data });
					}
				}
			});

			if (!echartsInstance) {
				return;
			}

			// click event to highlight specific point on chart
			if (onPointSelect) {
				echartsInstance.on(GRAPH_EVENT_TYPES.CLICK, (params) => {
					onPointSelect(params);
				});
			}

			// mouseover event to enable tooltip
			echartsInstance.on(GRAPH_EVENT_TYPES.MOUSE_OVER, activateTooltipOnMouseOver);

			// mouseout event to hide tooltip
			echartsInstance.on(GRAPH_EVENT_TYPES.MOUSE_OUT, hideTooltipOnMouseOut);
		},
		[onPointSelect, activateTooltipOnMouseOver, hideTooltipOnMouseOut, findSeries],
	);

	const initChartEventsOnMount = useCallback(
		(echartsInstance: EChartsInstance) => {
			chartRef.current = echartsInstance;
			registerEvents(echartsInstance);
		},
		[registerEvents],
	);

	useEffect(() => {
		if (chartRef.current) {
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			chartRef.current.off(GRAPH_EVENT_TYPES.CLICK);
			// scoped useState values doesn't seems to be updated when callback functions are triggered.
			// re-register event if callback function changes.
			chartRef.current &&
				onPointSelect &&
				// @ts-expect-error - TS2571 - Object is of type 'unknown'.
				chartRef.current.on(GRAPH_EVENT_TYPES.CLICK, onPointSelect);
		}
	}, [onPointSelect]);

	useEffect(
		() => () => {
			// un register all events on un-mount & dispose chart reference
			map(GRAPH_EVENT_TYPES, (event) => {
				// @ts-expect-error - TS2571 - Object is of type 'unknown'.
				chartRef.current && chartRef.current.off(event);
			});
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			chartRef.current && chartRef.current.dispose();
		},
		[],
	);

	return { initChartEventsOnMount, highlightPoint, downplayPoint, refreshOptions };
};
